import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Plus, Sparkles, IndianRupee, Repeat, FileWarning, PlayCircle,
  ChevronRight, ChevronDown, Download, X, Layers, ReceiptText, Gauge,
} from "lucide-react";

const TABS = ["overview", "contracts", "subscriptions", "rate-cards", "billing", "invoices", "reconciliation"];
const inr = (n) => (n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));
const nowPeriod = () => new Date().toISOString().slice(0, 7);
const INV_TINT = {
  DRAFT: "bg-slate-100 text-slate-600 border-slate-200",
  PENDING_APPROVAL: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED: "bg-blue-50 text-blue-700 border-blue-200",
  ISSUED: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Subscriptions() {
  const [tab, setTab] = useState("overview");
  const [meta, setMeta] = useState(null);
  const [ov, setOv] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [cards, setCards] = useState([]);
  const [runs, setRuns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(nowPeriod());
  const [dlg, setDlg] = useState(null);       // 'contract' | 'sub' | 'card' | 'run'
  const [activeInv, setActiveInv] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQ, setAiQ] = useState(""); const [aiAns, setAiAns] = useState(""); const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { api.get("/subscriptions/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async (t = tab) => {
    setLoading(true);
    try {
      if (t === "overview") setOv((await api.get("/subscriptions/overview")).data);
      else if (t === "contracts") setContracts((await api.get("/subscriptions/contracts")).data.items);
      else if (t === "subscriptions") setSubs((await api.get("/subscriptions/subscriptions")).data.items);
      else if (t === "rate-cards") setCards((await api.get("/subscriptions/rate-cards")).data.items);
      else if (t === "billing") setRuns((await api.get("/subscriptions/billing-runs")).data.items);
      else if (t === "invoices") setInvoices((await api.get("/subscriptions/draft-invoices")).data.items);
      else if (t === "reconciliation") setRecon((await api.get("/subscriptions/reconciliation", { params: { period } })).data);
    } catch { toast.error("Couldn't load data"); }
    finally { setLoading(false); }
  }, [tab, period]);

  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line

  const seed = async () => { await api.post("/subscriptions/seed-templates"); toast.success("Rate-card templates seeded"); load("rate-cards"); };
  const runBilling = async (commit) => {
    try {
      const url = commit ? "/subscriptions/billing-runs" : "/subscriptions/billing-runs/preview";
      const { data } = await api.post(url, { period });
      if (commit) { toast.success(`Run complete — ${data.created} draft invoice(s), ${data.skipped_existing} skipped`); setTab("invoices"); load("invoices"); }
      else setDlg({ kind: "preview", data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
  };
  const askAi = async () => {
    if (!aiQ.trim()) return; setAiBusy(true); setAiAns("");
    try { setAiAns((await api.post("/subscriptions/ai", { query: aiQ.trim() })).data.answer); }
    catch { setAiAns("AI unavailable."); } finally { setAiBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto p-5 space-y-5" data-testid="subscriptions-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Commercial</div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mt-1">Subscriptions &amp; Usage Billing</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Device rental / DaaS, managed service, warehousing, meter &amp; event billing — over your existing customers, assets, contracts &amp; finance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setAiOpen(true); setAiAns(""); setAiQ(""); }} data-testid="sub-ai-btn"><Sparkles className="h-4 w-4 mr-1 text-violet-600" /> Ask ONE AI</Button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`sub-tab-${t}`}
            className={"px-3 py-2 text-sm capitalize border-b-2 -mb-px " + (tab === t ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t.replace("-", " ")}
          </button>
        ))}
      </nav>

      {loading ? <div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
        <>
          {tab === "overview" && ov && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sub-overview">
              <Kpi icon={Repeat} label="Active MRR" value={inr(ov.mrr)} tone="text-emerald-700" />
              <Kpi icon={IndianRupee} label="ARR" value={inr(ov.arr)} />
              <Kpi icon={ReceiptText} label="Billed this month" value={inr(ov.billed_this_month)} />
              <Kpi icon={FileWarning} label="Billing exceptions" value={ov.billing_exceptions} tone={ov.billing_exceptions ? "text-rose-700" : ""} />
              <Kpi icon={Layers} label="Active contracts" value={ov.active_contracts} />
              <Kpi icon={Layers} label="Active subscriptions" value={ov.active_subscriptions} />
              <Kpi icon={Gauge} label="Unbilled events" value={ov.unbilled_events} />
            </div>
          )}

          {tab === "contracts" && (
            <Section title="Contracts" onAdd={meta?.can_write ? () => setDlg({ kind: "contract" }) : null} addLabel="New contract" testid="sub-add-contract">
              <Table cols={["Contract", "Customer", "Currency", "Frequency", "Status"]}
                rows={contracts.map((c) => [c.contract_no, c.customer_name, c.currency, c.billing_frequency,
                  <Badge className="text-[10px] border bg-emerald-50 text-emerald-700 border-emerald-200">{c.status}</Badge>])} empty="No contracts yet." />
            </Section>
          )}

          {tab === "subscriptions" && (
            <Section title="Subscriptions / Billable assets" onAdd={meta?.can_write ? () => setDlg({ kind: "sub" }) : null} addLabel="Add subscription" testid="sub-add-sub">
              <Table cols={["Serial / Ref", "Model", "Qty", "Rate", "Proration", "Ownership", "Status"]}
                rows={subs.map((s) => [s.serial || s.employee || "—", s.model, s.quantity, inr(s.rate), s.proration, s.ownership,
                  <Badge className="text-[10px] border bg-slate-100 text-slate-600 border-slate-200">{s.status}</Badge>])} empty="No subscriptions yet." />
            </Section>
          )}

          {tab === "rate-cards" && (
            <Section title="Rate cards" onAdd={meta?.can_write ? () => setDlg({ kind: "card" }) : null} addLabel="New rate card"
              extra={meta?.can_write && <Button variant="outline" size="sm" onClick={seed} data-testid="sub-seed">Seed templates</Button>} testid="sub-add-card">
              <Table cols={["Name", "Service", "Scope", "Unit", "Rate", "Tiers", "Tax"]}
                rows={cards.map((c) => [c.name, c.service, c.master_company_id ? "Customer" : "Global", c.unit, inr(c.rate),
                  (c.tiers?.length || 0) + " tiers", c.tax_rate + "%"])} empty="No rate cards — seed templates to start." />
            </Section>
          )}

          {tab === "billing" && (
            <div className="space-y-4" data-testid="sub-billing">
              <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-end gap-3 flex-wrap">
                <div><div className="text-[11px] uppercase text-slate-400 mb-1">Billing period</div>
                  <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 w-44" data-testid="sub-period" /></div>
                <Button variant="outline" onClick={() => runBilling(false)} data-testid="sub-preview-run"><PlayCircle className="h-4 w-4 mr-1" /> Preview</Button>
                {meta?.can_write && <Button onClick={() => runBilling(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="sub-create-run">Create draft invoices</Button>}
              </div>
              <Table cols={["Run", "Period", "Status", "Created", "Skipped"]}
                rows={runs.map((r) => [r.run_no, r.period, r.status, r.created_count ?? "—", r.skipped_count ?? "—"])} empty="No billing runs yet." />
            </div>
          )}

          {tab === "invoices" && (
            <Table cols={["Invoice", "Customer", "Period", "Subtotal", "Total", "Status"]} testid="sub-invoices"
              onRow={(i) => api.get(`/subscriptions/draft-invoices/${invoices[i].id}`).then((r) => setActiveInv(r.data))}
              rows={invoices.map((v) => [v.invoice_no, v.customer_name, v.period, inr(v.subtotal), inr(v.total),
                <Badge className={"text-[10px] border " + (INV_TINT[v.status] || INV_TINT.DRAFT)}>{v.status?.replace(/_/g, " ")}</Badge>])} empty="No draft invoices — run billing first." />
          )}

          {tab === "reconciliation" && recon && (
            <div className="space-y-3" data-testid="sub-recon">
              <div className="flex items-end gap-3">
                <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 w-44" />
                <Button variant="outline" onClick={() => load("reconciliation")}>Refresh</Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Kpi label="Billable not billed" value={recon.billable_not_billed_count} tone={recon.billable_not_billed_count ? "text-rose-700" : ""} />
                <Kpi label="Unbilled events" value={recon.unbilled_events} />
                <Kpi label="Unbilled meter reads" value={recon.unbilled_meter_readings} />
                <Kpi label="Missing rate" value={recon.missing_rate} tone={recon.missing_rate ? "text-amber-700" : ""} />
                <Kpi label="Duplicate billing" value={recon.duplicate_billing.length} tone={recon.duplicate_billing.length ? "text-rose-700" : ""} />
                <Kpi label="Billed after recovery" value={recon.billed_after_recovery.length} tone={recon.billed_after_recovery.length ? "text-rose-700" : ""} />
              </div>
              {recon.billable_not_billed.length > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <div className="text-sm font-semibold text-rose-800 mb-2">Billable but not billed ({recon.billable_not_billed_count})</div>
                  <div className="flex flex-wrap gap-1.5">{recon.billable_not_billed.slice(0, 40).map((r, i) => (
                    <Badge key={i} className="bg-white text-rose-700 border border-rose-200 text-[10px]">{r.serial} · {r.model}</Badge>))}</div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeInv && <InvoiceDrawer inv={activeInv} meta={meta} onClose={() => setActiveInv(null)} reload={() => { load("invoices"); setActiveInv(null); }} />}
      {dlg?.kind === "contract" && <ContractDialog onClose={() => setDlg(null)} onDone={() => { setDlg(null); load("contracts"); }} />}
      {dlg?.kind === "sub" && <SubDialog meta={meta} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load("subscriptions"); }} />}
      {dlg?.kind === "card" && <CardDialog meta={meta} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load("rate-cards"); }} />}
      {dlg?.kind === "preview" && <PreviewDialog data={dlg.data} onClose={() => setDlg(null)} />}

      <Dialog open={aiOpen} onOpenChange={(o) => !o && setAiOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="sub-ai-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Ask ONE AI · Billing</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">{["Which subscriptions renew next month?", "Show unbilled deployments", "Show billing exceptions", "How many devices are being billed?"].map((s) => (
              <button key={s} onClick={() => setAiQ(s)} className="text-[11px] px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">{s}</button>))}</div>
            <div className="flex gap-2"><Input value={aiQ} onChange={(e) => setAiQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAi()} placeholder="Ask about billing…" data-testid="sub-ai-input" />
              <Button onClick={askAi} disabled={aiBusy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="sub-ai-send">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask"}</Button></div>
            {aiAns && <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700" data-testid="sub-ai-answer">{aiAns}</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
    <div className={"mt-1 text-2xl font-semibold tabular-nums " + (tone || "text-slate-900")}>{value}</div>
  </div>
);
const Section = ({ title, onAdd, addLabel, extra, testid, children }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between"><div className="text-sm font-semibold text-slate-800">{title}</div>
      <div className="flex gap-2">{extra}{onAdd && <Button size="sm" onClick={onAdd} data-testid={testid} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1" />{addLabel}</Button>}</div></div>
    {children}
  </div>
);
const Table = ({ cols, rows, empty, onRow, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid={testid}>
    <div className="grid px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
      {cols.map((c) => <div key={c}>{c}</div>)}
    </div>
    {rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">{empty}</div> :
      rows.map((r, i) => (
        <div key={i} onClick={onRow ? () => onRow(i) : undefined} data-testid={`sub-tr-${i}`}
          className={"grid px-4 py-2.5 items-center border-b border-slate-50 text-sm " + (onRow ? "cursor-pointer hover:bg-slate-50" : "")}
          style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
          {r.map((cell, j) => <div key={j} className="truncate text-slate-700">{cell}</div>)}
        </div>
      ))}
  </div>
);

function InvoiceDrawer({ inv, meta, onClose, reload }) {
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(`/subscriptions/draft-invoices/${inv.id}/${path}`, body || {}); toast.success("Done"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const dl = async () => {
    try { const r = await api.get(`/subscriptions/draft-invoices/${inv.id}/schedule.xlsx`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `schedule_${inv.invoice_no}.xlsx`; a.click(); }
    catch { toast.error("Download failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="sub-inv-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between z-10">
          <div><div className="text-sm font-semibold text-slate-900">{inv.invoice_no}</div>
            <div className="text-[11px] text-slate-400">{inv.customer_name} · {inv.period} · {inv.contract_no}</div></div>
          <div className="flex items-center gap-2">
            <Badge className={"text-[10px] border " + (INV_TINT[inv.status] || INV_TINT.DRAFT)}>{inv.status?.replace(/_/g, " ")}</Badge>
            <button onClick={onClose} data-testid="sub-inv-close" className="p-1 rounded hover:bg-slate-100"><X className="h-4 w-4 text-slate-500" /></button>
          </div>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {(inv.lines || []).map((ln, i) => (
            <div key={i} className="rounded-lg border border-slate-200">
              <button onClick={() => setOpen({ ...open, [i]: !open[i] })} data-testid={`sub-line-${i}`}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 text-left">
                <div className="flex items-center gap-2 min-w-0">
                  {open[i] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{ln.description}</div>
                    <div className="text-[11px] text-slate-400">Qty {ln.qty} × {inr(ln.rate)} · {ln.source}{ln.note ? ` · ${ln.note}` : ""}</div></div>
                </div>
                <div className="text-right shrink-0"><div className="font-semibold tabular-nums">{inr(ln.amount)}</div>
                  {ln.rate_missing && <span className="text-[10px] text-rose-600">RATE MISSING</span>}</div>
              </button>
              {open[i] && (ln.drilldown || []).length > 0 && (
                <div className="border-t border-slate-100 px-3 py-2 bg-slate-50/60" data-testid={`sub-drill-${i}`}>
                  {ln.drilldown.map((d, j) => (
                    <div key={j} className="flex justify-between text-[12px] py-0.5 text-slate-600">
                      <span>{d.serial || d.meter || d.event || "—"}{d.employee ? ` · ${d.employee}` : ""}{d.days != null ? ` · ${d.days}d` : ""}{d.consumption != null ? ` · used ${d.consumption}` : ""}</span>
                      <span className="tabular-nums">{d.amount != null ? inr(d.amount) : (d.overage != null ? `overage ${d.overage}` : "")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
            <Row k="Subtotal" v={inr(inv.subtotal)} /><Row k="Tax" v={inr(inv.tax)} />
            <Row k="Total" v={inr(inv.total)} bold />
          </div>
          {meta?.can_write && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={dl} data-testid="sub-schedule-dl"><Download className="h-3.5 w-3.5 mr-1" /> Schedule (XLSX)</Button>
              {inv.status === "DRAFT" && <><Button size="sm" disabled={busy} onClick={() => act("adjust", { type: "CREDIT", amount: 0, reason: "review" })} data-testid="sub-adjust" variant="outline">Add credit ₹0</Button>
                <Button size="sm" disabled={busy} onClick={() => act("submit-approval")} data-testid="sub-submit-approval">Submit for approval</Button></>}
              {["DRAFT", "APPROVED"].includes(inv.status) && <Button size="sm" disabled={busy} onClick={() => act("issue")} data-testid="sub-issue" className="bg-emerald-600 hover:bg-emerald-700 text-white">Issue invoice</Button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
const Row = ({ k, v, bold }) => (<div className={"flex justify-between " + (bold ? "font-semibold text-slate-900 pt-1 border-t border-slate-200" : "text-slate-600")}><span>{k}</span><span className="tabular-nums">{v}</span></div>);

function Field({ label, children }) { return <div><div className="text-[11px] uppercase text-slate-400 mb-1">{label}</div>{children}</div>; }

function ContractDialog({ onClose, onDone }) {
  const [f, setF] = useState({ customer_name: "", start_date: new Date().toISOString().slice(0, 10), currency: "INR", billing_frequency: "MONTHLY" });
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.post("/subscriptions/contracts", f); toast.success("Contract created"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-md" data-testid="sub-contract-modal">
      <DialogHeader><DialogTitle>New contract</DialogTitle></DialogHeader>
      <div className="space-y-2.5">
        <Field label="Customer"><Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="sub-c-customer" /></Field>
        <Field label="Start date"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
        <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.customer_name} className="bg-slate-900 text-white" data-testid="sub-c-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></div>
      </div></DialogContent></Dialog>
  );
}
function SubDialog({ meta, onClose, onDone }) {
  const [contracts, setContracts] = useState([]);
  const [f, setF] = useState({ contract_id: "", model: "RENTAL", serial: "", rate: 3000, quantity: 1, billing_start: new Date().toISOString().slice(0, 10), proration: "DAILY", ownership: "SUNTRONIX" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/subscriptions/contracts").then((r) => { setContracts(r.data.items); if (r.data.items[0]) setF((x) => ({ ...x, contract_id: r.data.items[0].id })); }); }, []);
  const submit = async () => { setBusy(true); try { await api.post("/subscriptions/subscriptions", { ...f, rate: Number(f.rate), quantity: Number(f.quantity) }); toast.success("Subscription added"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-md" data-testid="sub-sub-modal">
      <DialogHeader><DialogTitle>Add subscription / billable asset</DialogTitle></DialogHeader>
      <div className="space-y-2.5">
        <Field label="Contract"><select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })} data-testid="sub-s-contract">{contracts.map((c) => <option key={c.id} value={c.id}>{c.contract_no} · {c.customer_name}</option>)}</select></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Model"><select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} data-testid="sub-s-model">{(meta?.models || []).map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select></Field>
          <Field label="Proration"><select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.proration} onChange={(e) => setF({ ...f, proration: e.target.value })}>{(meta?.proration || []).map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Serial / Ref"><Input value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} data-testid="sub-s-serial" /></Field>
          <Field label="Rate (₹)"><Input type="number" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="sub-s-rate" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Billing start"><Input type="date" value={f.billing_start} onChange={(e) => setF({ ...f, billing_start: e.target.value })} /></Field>
          <Field label="Ownership"><select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.ownership} onChange={(e) => setF({ ...f, ownership: e.target.value })}><option>SUNTRONIX</option><option>CUSTOMER</option></select></Field>
        </div>
        <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.contract_id} className="bg-slate-900 text-white" data-testid="sub-s-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button></div>
      </div></DialogContent></Dialog>
  );
}
function CardDialog({ meta, onClose, onDone }) {
  const [f, setF] = useState({ name: "", service: "RENTAL", unit: "device", rate: 0, tax_rate: 18 });
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.post("/subscriptions/rate-cards", { ...f, rate: Number(f.rate) }); toast.success("Rate card created"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-md" data-testid="sub-card-modal">
      <DialogHeader><DialogTitle>New rate card</DialogTitle></DialogHeader>
      <div className="space-y-2.5">
        <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sub-rc-name" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Service"><select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })}>{(meta?.models || []).map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select></Field>
          <Field label="Rate (₹)"><Input type="number" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="sub-rc-rate" /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.name} className="bg-slate-900 text-white" data-testid="sub-rc-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></div>
      </div></DialogContent></Dialog>
  );
}
function PreviewDialog({ data, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-2xl" data-testid="sub-preview-modal">
      <DialogHeader><DialogTitle>Billing preview · {data.period}</DialogTitle></DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        <div className="text-sm text-slate-600">{data.count} invoice(s) · grand total <strong>{inr(data.grand_total)}</strong></div>
        {data.invoices.map((inv) => (
          <div key={inv.contract_id} className="rounded-lg border border-slate-200 p-3">
            <div className="text-sm font-medium text-slate-800">{inv.contract_no} · {inv.customer_name}</div>
            {inv.lines.map((l, i) => <div key={i} className="flex justify-between text-[13px] text-slate-600 py-0.5"><span>{l.description} (qty {l.qty})</span><span className="tabular-nums">{inr(l.amount)}</span></div>)}
            <div className="flex justify-between text-sm font-semibold pt-1 border-t border-slate-100 mt-1"><span>Total</span><span>{inr(inv.total)}</span></div>
          </div>
        ))}
      </div>
    </DialogContent></Dialog>
  );
}
